import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  BehaviorSubject,
  catchError,
  filter,
  Observable,
  of,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import {
  CampaignLinkedinCredential,
  GeoCodeInfo,
} from 'src/app/shared/models/campaign/campaign-settings';
import { CampaignService } from 'src/app/shared/services/requests/campaign.service';
import { DeleteAccountModalComponent } from '../delete-account-modal';
import {
  AccountService,
  CampaignAccountsService,
  NotificationService,
} from 'src/app/shared/services';
import { LocationService } from 'src/app/core/services/global';
import { NotificationTypeEnum } from 'src/app/shared/enums';
import {
  AddLinkedinAccountModalComponent,
  AddLinkedinAccountModalData,
} from '../add-linkedin-account-modal';
import { ActivatedRoute, Params } from '@angular/router';
import {
  LinkedinAccountLimitsModalComponent,
  LinkedinLimitsData,
} from '../linkedin-account-limits-modal';
import {
  LinkedinAccountPrivacyModalComponent,
  LinkedinPrivacyData,
} from '../linkedin-account-privacy-modal';
import {
  LimitationsModalData,
  SimpleLimitationModalComponent,
} from '../../../../system-shared/components';
import { SystemPages } from '../../../../../shared/enums/routerPaths.enum';
import { AccountAccessInfo } from '../../../../../shared/models';
import { LimitationService } from '../../../../system-shared/services/limitation.service';
import { map } from 'rxjs/operators';

interface CampaignLinkedinCredentialClient extends CampaignLinkedinCredential {
  canAccessPersonally: boolean;
}

@Component({
  selector: 'app-linkedin-accounts',
  templateUrl: './linkedin-accounts.component.html',
  styleUrls: ['./linkedin-accounts.component.scss'],
  standalone: false,
})
export class LinkedinAccountsComponent implements OnInit, OnDestroy {
  constructor(
    private campaignService: CampaignService,
    private dialog: MatDialog,
    private notificationService: NotificationService,
    private activatedRoute: ActivatedRoute,
    private locationService: LocationService,
    private accountService: AccountService,
    private limitationService: LimitationService,
    private campaignAccountsService: CampaignAccountsService,
  ) {}

  private $unsubscribe = new Subject<void>();
  private geoCodeListCache: GeoCodeInfo[] | null = null;
  private canAddAccount = false;
  private isLimited = false;

  private listSrc$ = new BehaviorSubject<CampaignLinkedinCredentialClient[]>([]);
  lockActions$ = new BehaviorSubject<boolean>(false);
  list$ = this.listSrc$.asObservable();

  status: 'loading' | 'empty' | 'received' = 'loading';

  ngOnInit(): void {
    this.accountService
      .getLinkedInAccountAccessInfo$()
      .pipe(
        filter((accessInfo: AccountAccessInfo) => !!accessInfo),
        takeUntil(this.$unsubscribe),
      )
      .subscribe((accessInfo: AccountAccessInfo) => {
        this.canAddAccount = accessInfo.canAddAccount;
        this.isLimited = accessInfo.isLimited;
      });

    this.handleQueryParams();
    this.updateList();
  }

  toggleAccountEnable(value: CampaignLinkedinCredential): void {
    this.campaignService
      .toggleEnableInLdAccount(value.id, !value.enable)
      .pipe(
        takeUntil(this.$unsubscribe),
        catchError((err) => {
          return of(null);
        }),
      )
      .subscribe((response) => {
        if (!response) return;

        const updatedList = this.listSrc$
          .getValue()
          .map((account) =>
            account.id === value.id ? { ...account, enable: !value.enable } : account,
          );
        this.listSrc$.next(updatedList);
      });
  }

  onAddAccountClick(): void {
    if (this.isLimited) {
      this.limitationService.onPlanLimit();
    } else if (this.canAddAccount) {
      this.addAccount();
    } else {
      this.openNoSlotsModal();
    }
  }

  reconnect(credential: CampaignLinkedinCredential): void {
    this.addAccount(false, credential.login);
  }

  editLimits(credential: CampaignLinkedinCredential): void {
    this.lockActions$.next(true);

    const limitsData: LinkedinLimitsData = {
      views: credential.views,
      likes: credential.likes,
      requests: credential.requests,
      messages: credential.messages,
      inMail: credential.inMail,
      withdrawLimit: credential.withdrawLimit,
    };

    this.dialog
      .open(LinkedinAccountLimitsModalComponent, { data: limitsData })
      .afterClosed()
      .pipe(
        filter((limitsData: LinkedinLimitsData | null) => {
          if (!limitsData) this.lockActions$.next(false);
          return !!limitsData;
        }),
        switchMap((limitsData: LinkedinLimitsData) => {
          const updatedCredential: CampaignLinkedinCredential = {
            ...credential,
            ...limitsData,
          };
          return this.campaignService.updateLinkedinCredential(updatedCredential);
        }),
        takeUntil(this.$unsubscribe),
        tap(() => {
          this.updateList();
          this.notificationService.notify(
            NotificationTypeEnum.SUCCESS,
            'CAMPAIGN.ACCOUNTS.LINKEDIN_ACCOUNTS.LIMITS_UPDATED_NOTIFICATION',
          );
        }),
        catchError(() => of(null)),
      )
      .subscribe(() => {
        this.lockActions$.next(false);
      });
  }

  editPrivacy(credential: CampaignLinkedinCredential): void {
    this.lockActions$.next(true);

    this.getGeoCodeList()
      .pipe(
        switchMap((geoCodeList: GeoCodeInfo[]) => {
          const privacyData: LinkedinPrivacyData = {
            geoCode: credential.geoCode,
            allowTeamAccess: credential.allowTeamAccess,
            geoCodeList,
            canAllowTeamAccess: this.campaignAccountsService.isCurrentUserAccount(credential),
          };

          return this.dialog
            .open(LinkedinAccountPrivacyModalComponent, { data: privacyData })
            .afterClosed();
        }),
        filter((privacyData: LinkedinPrivacyData | null) => {
          if (!privacyData) this.lockActions$.next(false);
          return !!privacyData;
        }),
        switchMap((privacyData: LinkedinPrivacyData) => {
          const updatedCredential: CampaignLinkedinCredential = {
            ...credential,
            geoCode: privacyData.geoCode,
            allowTeamAccess: privacyData.allowTeamAccess,
          };
          return this.campaignService.updateLinkedinCredential(updatedCredential);
        }),
        takeUntil(this.$unsubscribe),
        tap(() => {
          this.updateList();
          this.notificationService.notify(
            NotificationTypeEnum.SUCCESS,
            'CAMPAIGN.ACCOUNTS.LINKEDIN_ACCOUNTS.PRIVACY_UPDATED_NOTIFICATION',
          );
        }),
        catchError(() => of(null)),
      )
      .subscribe(() => {
        this.lockActions$.next(false);
      });
  }

  delete(credendial: CampaignLinkedinCredential): void {
    this.dialog
      .open(DeleteAccountModalComponent, { data: credendial.name })
      .afterClosed()
      .pipe(
        filter((deletionConfirmed: boolean) => deletionConfirmed),
        switchMap(() => {
          this.lockActions$.next(true);
          return this.campaignService.deleteLinkedinCredential(credendial.id);
        }),
        takeUntil(this.$unsubscribe),
        tap(() => {
          this.updateList();
          this.notificationService.notify(NotificationTypeEnum.SUCCESS, 'Linkedin account deleted');
        }),
        catchError(() => of(null)),
      )
      .subscribe(() => {
        this.lockActions$.next(false);
      });
  }

  private openNoSlotsModal(): void {
    const noSlotsTranslationKey = 'CAMPAIGN.ACCOUNTS.LINKEDIN_ACCOUNTS.NO_SLOTS_MODAL';
    const data: LimitationsModalData = {
      title: `${noSlotsTranslationKey}.TITLE`,
      description: `${noSlotsTranslationKey}.DESCRIPTION`,
      action: {
        text: `${noSlotsTranslationKey}.ADD_BUTTON`,
        routerLink: SystemPages.BILLING,
        routerParams: { openModal: 'buyLinkedinSlots' },
      },
    };

    this.dialog.open(SimpleLimitationModalComponent, { data });
  }

  private addAccount(closePageWhenDone = false, reconnectLogin?: string): void {
    const data: AddLinkedinAccountModalData = {
      extensionInstalled: true,
      login: reconnectLogin,
    };

    this.dialog
      .open(AddLinkedinAccountModalComponent, { data })
      .afterClosed()
      .pipe(
        tap(() => {
          if (closePageWhenDone) {
            window.close();
          }
        }),
      )
      .subscribe((needToUpdateList: boolean) => {
        if (needToUpdateList) {
          this.updateList();
        }
      });
  }

  private updateList(): void {
    this.status = 'loading';
    this.campaignService
      .getLinkedinCredentials()
      .pipe(
        take(1),
        map((list: CampaignLinkedinCredential[]) => (list?.length ? list : [])),
        tap((list: CampaignLinkedinCredential[]) => {
          this.status = list.length ? 'received' : 'empty';
        }),
        catchError(() => of([])),
      )
      .subscribe((credentials: CampaignLinkedinCredential[]) => {
        const clientCredentials: CampaignLinkedinCredentialClient[] = credentials.map((item) => ({
          ...item,
          canAccessPersonally: this.campaignAccountsService.canAccessAccountPersonally(item),
        }));
        this.listSrc$.next(clientCredentials);
      });
  }

  private handleQueryParams(): void {
    this.activatedRoute.queryParams
      .pipe(takeUntil(this.$unsubscribe))
      .subscribe((params: Params) => {
        if ('linkedinConnected' in params && params['linkedinConnected'] === 'true') {
          this.notificationService.notify(
            NotificationTypeEnum.SUCCESS,
            'ACCOUNTS_SETTINGS.LINKEDIN.CONNECTION_SUCCEDED_NOTIFICATION',
          );
        }

        if ('addAccount' in params) {
          this.locationService.replaceQuery('');
          // queryParams data returns as a string, that's why we compare 'true'
          const closeAfter = params['closeAfterDone'] === 'true';
          this.addAccount(closeAfter);
        }

        this.locationService.replaceQuery('tab=linkedin');
      });
  }

  private getGeoCodeList(): Observable<GeoCodeInfo[]> {
    if (this.geoCodeListCache) return of(this.geoCodeListCache);

    return this.campaignService.getGeoCodeList().pipe(
      tap((list: GeoCodeInfo[]) => {
        this.geoCodeListCache = list;
      }),
    );
  }

  ngOnDestroy(): void {
    this.$unsubscribe.next();
    this.$unsubscribe.complete();
  }
}
